import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * SoftRibbon — sits in reserved white space directly above the greenhouse image.
 *
 * Timing (slow + luxurious — user must be able to read comfortably):
 *   0.00s               mount: sentence + dot + CTA at opacity 0, translated -50px left
 *   0.00–3.00s          slide in from left + fade in (easeOut, 3s)
 *   3.00–10.00s         hold fully still (7s of comfortable reading)
 *   10.00–14.00s        sentence + dot slowly fade to 0 (4s, gentle scale 1.00 → 1.03)
 *   after 14.00s        only "Notify me →" remains, stationary, live, functional
 *
 * Replay: parent mounts with a fresh key on every /homepage entry so the
 * whole sequence replays. No session/local/cookie suppression — animation plays
 * every visit / refresh / return to home.
 */
export function SoftRibbon({ start = false }) {
  const [started, setStarted] = useState(false);
  const [sentenceGone, setSentenceGone] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Wait until the splash curtain is gone before starting.
  useEffect(() => {
    if (start) {
      setStarted(true);
    }
  }, [start]);
  useEffect(() => {
    if (!started) return;
    // Enter 3.0s + hold 7.0s = 10.0s → begin sentence fade.
    const t = setTimeout(() => setSentenceGone(true), 10000);
    return () => clearTimeout(t);
  }, [started]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      await api.post("/waitlist", { email, role: "launch" });
      setSent(true);
      setTimeout(() => setOpen(false), 1400);
      toast.success("You're on the list.");
    } catch {
      toast.error("Something went wrong. Try again?");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="soft-ribbon" className="relative w-full flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={started ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
        transition={{ duration: 3.0, ease: [0.22, 1, 0.36, 1] }}
        className="flex items-center gap-3 text-[#7A7F79] leading-none"
      >
        <motion.span
          animate={
            sentenceGone
              ? { opacity: 0, scale: 1.03 }
              : { opacity: 1, scale: 1 }
          }
          transition={{ duration: 4.0, ease: "easeOut" }}
          style={{ display: "inline-block", transformOrigin: "center" }}
          className="font-body font-light italic text-[1.0625rem] leading-none"
        >
          Quietly taking root, the greenhouse is stirring.
        </motion.span>
        <motion.span
          aria-hidden="true"
          animate={
            sentenceGone
              ? { opacity: 0, scale: 1.03 }
              : { opacity: 1, scale: 1 }
          }
          transition={{ duration: 4.0, ease: "easeOut" }}
          style={{ display: "inline-block", transformOrigin: "center" }}
          className="text-[#B7BAB4] text-[1.0625rem] leading-none"
        >
          ·
        </motion.span>
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="ribbon-notify-toggle"
          className="font-body text-[0.9rem] tracking-widest-2 uppercase text-[#7A7F79] hover:text-[var(--gu-primary)] transition-colors leading-none"
        >
          Notify me →
        </button>
      </motion.div>

      {/* Email form overlays via absolute positioning so it never pushes layout. */}
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-full max-w-sm z-10 bg-[var(--gu-bg)] px-4 py-3 border border-[var(--gu-border)] shadow-sm">
          <form onSubmit={submit} data-testid="ribbon-notify-form">
            {sent ? (
              <div className="font-display italic text-center text-[var(--gu-primary)]">
                Thank you. We'll write when the greenhouse opens.
              </div>
            ) : (
              <div className="flex items-end border-b border-[var(--gu-border)] focus-within:border-[var(--gu-primary)] transition-colors">
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your email"
                  data-testid="ribbon-email-input"
                  className="flex-1 bg-transparent outline-none border-0 py-2 text-base"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="ribbon-notify-submit"
                  className="overline text-[#7A7F79] hover:text-[var(--gu-primary)] pl-4 pb-2 transition-colors disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send →"}
                </button>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
